import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { useModules } from '../hooks/useModules';
import RiadSwitcher from './RiadSwitcher';
import { BRANDING } from '../config/branding';
import { 
  LayoutDashboard, 
  CalendarDays, 
  BookOpen, 
  Users, 
  ShoppingCart, 
  Package, 
  Receipt, 
  Wallet,
  UserCog,
  BarChart3,
  Settings,
  LogOut,
  X,
  UsersRound,
  Building2,
  FileText,
  Truck,
  UserCheck,
  ChevronDown,
  Banknote,
  Shield,
  User
} from 'lucide-react';

// Navigation items with required permissions and module dependencies
// Items with 'children' will render as submenus
const navItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, permissions: [], module: null },
  { path: '/calendar', label: 'Calendrier', icon: CalendarDays, permissions: ['reservations.view'], module: 'calendar' },
  { path: '/reservations', label: 'Réservations', icon: BookOpen, permissions: ['reservations.view'], module: 'reservations' },
  { 
    id: 'clients-menu',
    label: 'Clients', 
    icon: Users, 
    permissions: ['guests.view'],
    module: 'guests',
    children: [
      { path: '/guests', label: 'Liste des clients', icon: Users },
      { path: '/pax', label: 'Voyageurs (PAX)', icon: UserCheck }
    ]
  },
  { path: '/sales', label: 'Ventes', icon: ShoppingCart, permissions: ['sales.view'], module: 'pos' },
  { path: '/products', label: 'Produits & Services', icon: Package, permissions: ['products.view'], module: 'pos' },
  { path: '/expenses', label: 'Dépenses', icon: Receipt, permissions: ['expenses.view'], module: 'expenses' },
  { path: '/charges', label: 'Charges', icon: FileText, permissions: ['charges.view'], module: 'charges' },
  { path: '/suppliers', label: 'Fournisseurs', icon: Truck, permissions: ['suppliers.view', 'expenses.view'], module: 'expenses' },
  { path: '/payments', label: 'Encaissements', icon: Banknote, permissions: ['finance.view'], module: 'payments' },
  { path: '/payouts', label: 'Versements', icon: Building2, permissions: ['finance.view'], module: 'payouts' },
  { path: '/cash', label: 'Fond de caisse', icon: Wallet, permissions: ['cash.view'], module: 'payments' },
  { path: '/employees', label: 'RH', icon: UserCog, permissions: ['hr.view', 'hr.clock'], module: 'hr' },
  { path: '/reports', label: 'Rapports', icon: BarChart3, permissions: ['reports.view'], module: 'reports' },
  { path: '/users', label: 'Utilisateurs', icon: UsersRound, permissions: ['users.view'], module: null },
  { path: '/settings', label: 'Paramètres', icon: Settings, permissions: ['settings.view'], module: null },
];

export const Sidebar = ({ open, onClose }) => {
  const { user, logout, canAny } = useAuth();
  const { modules, loading: modulesLoading } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Toggle submenu
  const toggleMenu = (menuId) => {
    setOpenMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // Check if a submenu item is active
  const isSubmenuActive = (children) => {
    return children?.some(child => location.pathname === child.path);
  };

  // Check if module is enabled
  const isModuleEnabled = (moduleKey) => {
    if (!moduleKey) return true; // No module dependency = always visible
    // If modules are still loading, show all items (will filter on reload)
    if (modulesLoading) return true;
    return modules[moduleKey] !== false;
  };

  // Filter navigation items based on permissions AND modules
  const visibleNavItems = navItems.filter(item => {
    // Check module first (respects loading state)
    if (!isModuleEnabled(item.module)) return false;
    
    // Then check permissions
    if (!item.permissions || item.permissions.length === 0) return true;
    return canAny(item.permissions);
  });

  // Get role display name
  const getRoleDisplay = () => {
    if (!user?.role) return '';
    return user.role;
  };

  return (
    <>
      {/* Overlay for mobile */}
      {open && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={onClose}
        />
      )}
      
      <aside className={`sidebar ${open ? 'open' : ''}`} data-testid="sidebar">
        <div className="h-full flex flex-col">
          {/* Brand Header - Premium Look */}
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-3 flex-1">
                <img 
                  src={BRANDING.logo} 
                  alt={BRANDING.name} 
                  className="w-10 h-10 object-contain drop-shadow-sm" 
                  data-testid="sidebar-logo"
                />
                <div className="flex flex-col">
                  <span className="font-bold text-slate-900 text-lg leading-tight">{BRANDING.name}</span>
                  <span className="text-[10px] text-slate-400 tracking-wider uppercase">Gestion hôtelière</span>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 ml-auto"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <RiadSwitcher 
              currentRiad={user?.current_riad}
              accesses={user?.accesses || []}
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {visibleNavItems.map((item) => {
                // Render submenu
                if (item.children) {
                  const isOpen = openMenus[item.id] || isSubmenuActive(item.children);
                  const hasActiveChild = isSubmenuActive(item.children);
                  
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => {
                          // Navigate to first child (Liste des clients) and open submenu
                          if (item.children && item.children.length > 0) {
                            navigate(item.children[0].path);
                            onClose?.();
                          }
                          setOpenMenus(prev => ({ ...prev, [item.id]: true }));
                        }}
                        className={`
                          w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                          transition-colors duration-200
                          ${hasActiveChild ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-100'}
                        `}
                        data-testid={`nav-menu-${item.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className="w-5 h-5" />
                          {item.label}
                        </div>
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMenu(item.id);
                          }}
                        />
                      </button>
                      {isOpen && (
                        <ul className="mt-1 ml-4 space-y-1 border-l-2 border-gray-100 pl-2">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <NavLink
                                to={child.path}
                                onClick={onClose}
                                className={({ isActive }) => `
                                  flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                                  transition-colors duration-200
                                  ${isActive 
                                    ? 'bg-primary text-white' 
                                    : 'text-gray-600 hover:bg-gray-100'
                                  }
                                `}
                                data-testid={`nav-${child.path.replace('/', '')}`}
                              >
                                <child.icon className="w-4 h-4" />
                                {child.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                
                // Render regular menu item
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) => `
                        flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                        transition-colors duration-200
                        ${isActive 
                          ? 'bg-primary text-white' 
                          : 'text-gray-700 hover:bg-gray-100'
                        }
                      `}
                      data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-100">
            {/* Account Portal Link - for owners and super admins */}
            {(user?.is_owner || user?.is_super_admin) && (
              <NavLink
                to="/account"
                onClick={onClose}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium mb-3
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-amber-600 text-white' 
                    : 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                  }
                `}
                data-testid="nav-account"
              >
                <User className="w-5 h-5" />
                Mon Compte
              </NavLink>
            )}

            {/* Super Admin Link */}
            {user?.is_super_admin && (
              <NavLink
                to="/admin"
                onClick={onClose}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium mb-3
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-purple-600 text-white' 
                    : 'text-purple-700 bg-purple-50 hover:bg-purple-100'
                  }
                `}
                data-testid="nav-admin"
              >
                <Shield className="w-5 h-5" />
                Administration
              </NavLink>
            )}

            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-muted-foreground capitalize">{getRoleDisplay()}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
